// ─── When the running build is no longer the deployed one ───────────────────────
// Every screen is a lazy import, so opening one fetches a hashed chunk
// (/assets/Payments-<hash>.js) at click time rather than up front. A deploy
// rewrites those hashes and the previous build's files stop existing the moment
// Vercel promotes the new one — so any tab that predates the deploy asks for a file
// that is no longer there.
//
// It reads as random because only the screens NOT yet opened in that session are
// missing: the ones already in the module cache keep working, so the same left-hand
// menu half works and half does not.
//
// Nothing here reloads on its own, and that is the point. A reload is the correct
// repair, but it is also destructive: the assistant panel's thread and composer
// survive navigation by design, voice-note transcripts are held in memory and
// never stored, the copied-client chip and the note customer pin are deliberately
// session-scoped, and a customer part-way through a public intake form has answers
// in nothing but the DOM. Reloading silently to fix a menu click can therefore
// throw away a twenty-minute note or a half-filled medical questionnaire, and a
// page that spontaneously reloads mid-work reads as a crash anyway.
//
// So the failure is turned into a StaleBuildError, the ErrorBoundary explains it in
// its own words, and the person decides when to reload — which also lets them go
// back to a screen that still works and copy anything out first.

/**
 * Thrown when a route's chunk is missing because the build it belongs to has been
 * replaced. Not a fault: the app is working, the bundle it is holding has aged out.
 */
export class StaleBuildError extends Error {
  constructor(cause: unknown) {
    super('this screen belongs to a build that is no longer deployed', { cause })
    this.name = 'StaleBuildError'
  }
}

/** How long a newer service worker gets to claim the page before we reload anyway. */
const SW_TAKEOVER_TIMEOUT_MS = 1500

// None of these strings is specified anywhere and each engine words it
// differently, so match every known form rather than one browser's.
const STALE_CHUNK_PATTERNS = [
  'failed to fetch dynamically imported module', // Chrome, Edge
  'error loading dynamically imported module',   // Firefox
  'importing a module script failed',            // Safari
  'failed to load module script',                // the MIME-type refusal
  'expected a javascript module script',         // …and its longer form
  'unable to preload',                           // Vite's preload helper
]

/**
 * True when an import failed because the chunk could not be loaded, rather than
 * because the module itself threw.
 */
export function isStaleChunkError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? '')
  const haystack = message.toLowerCase()
  return STALE_CHUNK_PATTERNS.some((pattern) => haystack.includes(pattern))
}

/**
 * Lets a waiting service worker claim the page before a reload.
 *
 * The precached index.html is exactly as old as the chunk it points at, so a
 * reload served by the outgoing worker hands back the same stale document and the
 * same missing chunk — the reload would appear to do nothing. sw.js is served
 * `must-revalidate`, so `update()` picks up the new one, and the generated worker's
 * skipWaiting + clientsClaim do the rest.
 *
 * Best-effort and time-boxed: a worker that never turns over must not strand
 * somebody on a screen they have asked to reload.
 */
async function letNewWorkerTakeOver(): Promise<void> {
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
    const registration = await navigator.serviceWorker.getRegistration()
    if (!registration) return

    const claimed = new Promise<void>((resolve) => {
      navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true })
    })
    const timedOut = new Promise<void>((resolve) => setTimeout(resolve, SW_TAKEOVER_TIMEOUT_MS))

    await registration.update()
    await Promise.race([claimed, timedOut])
  } catch {
    // An unsupported or unreachable worker is not a reason to skip the reload.
  }
}

/**
 * Reloads onto the currently deployed build. Only ever called from a control the
 * person pressed themselves.
 */
export async function refreshToLatestBuild(): Promise<void> {
  await letNewWorkerTakeOver()
  window.location.reload()
}
